// The Climate Change page: what has happened to the region since the film was
// made, and who is trying to do something about it.
//
// Every card links out. The images were supplied by Elaine from the articles
// themselves; if any rights holder objects, remove the image and the card
// still works, since the layout does not depend on one being present.

export const climateHeader = {
  image: '/assets/gap/climate-header.jpg',
  portrait: '/assets/gap/climate-header-portrait.jpg',
  focus: '62% 50%',
  alt: 'Hillary O\u2019Sullivan playing a grand piano on Arctic pack ice',
}

export const climateGroups = [
  {
    title: 'What is happening',
    items: [
      {
        title: 'What is a glacial collapse, and how common is it?',
        source: 'Al Jazeera',
        date: 'August 2026',
        text: 'A section of glacier broke away above the Lhende river in Tibet. The ice and rock dammed the river, and when the blockage gave way the surge tore into Nepal\u2019s Rasuwa district. The Trishuli rose nine metres in thirty minutes.',
        image: '/assets/climate/nepal-flood.jpg',
        alt: 'Buildings buried in mud after the glacial flood in Rasuwa, Nepal, August 2026',
        url: 'https://www.aljazeera.com/features/2026/8/27/nepal-tibet-floods-what-is-a-glacial-collapse-how-common-is-it',
      },
      {
        title: 'Himalayan glaciers are losing ice at double the rate since 2000',
        source: 'ICIMOD',
        date: 'March 2026',
        text: 'Fifty years of monitoring across the Hindu Kush Himalaya. Up to 27 metres of ice thickness gone since 1975, and nearly two billion people downstream. Bhutan is named as one of the region\u2019s blind spots, largely unmonitored.',
        image: '/assets/climate/glaciers-report.jpg',
        alt: 'A glacier valley in the Hindu Kush Himalaya, with figures crossing the ice',
        url: 'https://www.icimod.org/press-releases/hindu-kush-himalaya-glaciers-losing-ice-at-double-the-rate-since-2000-new-icimod-report-confirm/',
      },
      {
        title: 'Families moved out of reach of the lakes, and still in tents',
        source: 'The Bhutanese',
        date: 'January 2025',
        text: 'Residents of Thangza and Toenchoe in Lunana relocated to escape a glacial lake outburst. A winter later they were living under plastic sheeting without title to the new land. The nearest road is a seven-hour walk.',
        image: '/assets/climate/lunana-tents.jpg',
        alt: 'Tents at a relocation site in Lunana, Bhutan, after families moved to escape a glacial lake outburst flood',
        url: 'https://thebhutanese.bt/glof-relocation-efforts-in-lunana-hampered-by-lack-of-support-and-resources/',
      },
      {
        title: 'Thorthormi Lake and the growing risk of glacial floods',
        source: 'The Bhutanese',
        date: 'September 2025',
        text: 'The moraine wall holding Thorthormi apart from Raphstreng has thinned from 45 metres to 33. A siphon is being trialled that needs no fuel and can work through winter, because the lake no longer freezes solid.',
        image: '/assets/climate/thorthormi-lakes.jpg',
        alt: 'Satellite view of Thorthormi, Raphstreng and Luggye glacial lakes above the Pho Chu river, Bhutan',
        url: 'https://thebhutanese.bt/himalayan-warnings-thorthormi-lake-and-the-growing-risk-of-glacial-floods/',
      },
    ],
  },
  {
    title: 'The wider problem',
    items: [
      {
        title: 'A shared risk across the Hindu Kush Himalayas',
        source: 'Stockholm Environment Institute',
        date: '2025',
        text: 'Three sensor-based early warning systems exist in the whole region, two of them in Nepal, and none operating across a border. The mechanisms that do exist were built for monsoon floods, where there are hours of warning. A glacier collapse gives minutes.',
        image: '/assets/climate/himalaya-aerial.jpg',
        alt: 'Aerial view across the Himalaya, a glacier running between snow peaks',
        url: 'https://www.sei.org/features/glacial-lake-outburst-floods-hindu-kush-himalayas/',
      },
    ],
  },
  {
    title: 'Bhutan and its glaciers',
    items: [
      {
        title: 'Living in constant fear of a glacial lake outburst flood',
        source: 'UNDP Bhutan',
        date: '',
        text: 'Lunana, a remote highland community nestled among glaciers, where climate change is an everyday reality.',
        image: '/assets/climate-awareness/image8.png',
        alt: 'Lunana, a highland community in Bhutan surrounded by glaciers',
        url: 'https://undp-bhutan.exposure.co/living-in-constant-fear-of-glacial-lake-outburst-flood',
      },
      {
        title: 'Glacial lake floods prevented in Bhutan',
        source: 'UNDP',
        date: '',
        text: 'Every year from June to September, between two and three hundred people trek to Thorthormi glacial lake to drain water from it by hand, with pickaxes and shovels.',
        image: '/assets/climate-awareness/image7.png',
        alt: 'Workers draining water from Thorthormi glacial lake by hand',
        url: 'https://www.youtube.com/watch?v=lbgv4ZMF5Zk',
      },
      {
        title: 'Dwindling cordyceps and a bleak future for Lunana\u2019s women',
        source: 'UNDP Bhutan',
        date: '',
        text: 'Lunana has warmed a great deal in the last decade, and the cordyceps harvest that sustains its households is disappearing with the cold.',
        image: '/assets/climate-awareness/image2.png',
        alt: 'Women of Lunana, Bhutan, whose livelihoods depend on the cordyceps harvest',
        url: 'https://undp-bhutan.exposure.co/climate-change-exacerbates-lunana-womens-insecurity',
      },
      {
        title: 'Training Bhutanese experts to analyse climate risk',
        source: 'Eurac Research',
        date: '',
        text: 'A research team travels to Bhutan to train local experts, and returns with the view that the Himalayan state has a great deal to teach.',
        image: '/assets/climate-awareness/image1.png',
        alt: 'Researchers working on climate risk analysis in Bhutan',
        url: 'https://www.eurac.edu/en/magazine/climate-risks-the-case-of-bhutan',
      },
      {
        title: 'Melting glaciers in Bhutan',
        source: 'BBC',
        date: '',
        text: 'Ade Adepitan visits Bhutan to see the effect of rising temperatures on its glaciers, and the danger that poses downstream.',
        image: '/assets/climate-awareness/image3.png',
        alt: 'Glaciers in Bhutan, filmed for the BBC',
        url: 'https://www.bbc.co.uk/teach/class-clips-video/articles/zg4ntrd',
      },
    ],
  },
  {
    title: 'Further afield',
    items: [
      {
        title: 'Scientists looked deep underneath the Doomsday Glacier',
        source: 'CNN',
        date: 'September 2024',
        text: 'What they found spells potential disaster for the planet.',
        image: '/assets/climate-awareness/image6.jpg',
        alt: 'The Thwaites Glacier in Antarctica, known as the Doomsday Glacier',
        url: 'https://edition.cnn.com/2024/09/20/climate/doomsday-glacier-thwaites-melt-sea-level-rise/index.html',
      },
      {
        title: 'Millions of children affected by flooding in South Asia',
        source: 'UNICEF',
        date: '',
        text: 'Flooding at this time of year is common. Its scale in mid-July was not.',
        image: '/assets/climate-awareness/image5.png',
        alt: 'Children affected by flooding in South Asia',
        url: 'https://www.unicef.org/press-releases/millions-children-affected-devastating-flooding-south-asia-many-more-risk-covid-19',
      },
    ],
  },
  {
    title: 'Worth your time',
    items: [
      {
        title: 'The Last Glaciers',
        source: 'Craig Leeson and Malcolm Wood',
        date: '2022',
        text: 'Filmed over four years in twelve countries, on the relationship between climate change, mountain environments and glaciers. From the director of A Plastic Ocean.',
        image: '/assets/climate/last-glaciers.jpg',
        alt: 'The Last Glaciers, a documentary on climate change and mountain glaciers',
        url: 'https://thelastglaciers.com/',
      },
      {
        title: 'The Royal Highland Festival',
        source: 'Laya, Gasa',
        date: 'Every October',
        text: 'Where the film was made. Founded in 2016 under the Royal Vision, it gathers highland communities from eleven gewogs across ten dzongkhags, whose way of life depends on the ice above them.',
        image: '/assets/climate/laya-festival.jpg',
        alt: 'Dancers in traditional dress at the Royal Highland Festival in Laya, Bhutan',
        url: 'https://laya.bt/',
      },
    ],
  },
]
